// File: frontend/src/utils/adminDates.js
//
// One place to turn a server value into text, because the admin dashboard used
// to have several and they disagreed. In a single table row, three lines apart,
// "Joined" ran through `new Date(x).toLocaleDateString()` while "Last active"
// printed the raw string — so one was shifted into the admin's timezone and the
// other was not, and the two dates being compared were never in the same frame
// of reference.
//
// There are two genuinely different kinds of value here and conflating them is
// what caused the confusion:
//
//   INSTANT       a moment on the server clock (users.created_at,
//                 devices.last_seen_at, completions.created_at). Belongs in the
//                 reader's timezone.
//   CALENDAR DAY  a date the user picked (completions.date, and anything that
//                 came out of a SQL DATE(...) bucket). It has no time and no
//                 zone; converting it shifts it by a day and means nothing.

// Server timestamps arrive in two shapes. SQLite hands back
// 'YYYY-MM-DD HH:MM:SS' — UTC, but space-separated with no zone marker, which
// Date() then parses as LOCAL and silently shifts by the reader's offset.
// node-postgres has already built a Date, which serializes to ISO with a Z.
// (Same normalization as the backend's routes/support.js toMs().)
export function toMs(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return value.getTime();
  const s = String(value);
  const ms = /[TZ]|[+-]\d{2}:?\d{2}$/.test(s)
    ? Date.parse(s)
    : Date.parse(s.replace(' ', 'T') + 'Z');
  return Number.isNaN(ms) ? null : ms;
}

export function toDate(value) {
  const ms = toMs(value);
  return ms === null ? null : new Date(ms);
}

// An absolute instant, in the reader's timezone.
export function formatInstant(value, { withTime = true } = {}) {
  const d = toDate(value);
  if (!d) return null;
  return withTime ? d.toLocaleString() : d.toLocaleDateString();
}

// A calendar day, rendered WITHOUT any timezone conversion. Takes the date
// parts off the front of the string rather than parsing it, because parsing
// 'YYYY-MM-DD' as a Date puts it at UTC midnight, which then renders as the
// previous day for anyone west of Greenwich.
export function formatCalendarDate(value) {
  if (!value) return null;
  const key = value instanceof Date
    ? [value.getFullYear(), String(value.getMonth() + 1).padStart(2, '0'),
       String(value.getDate()).padStart(2, '0')].join('-')
    : String(value).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return String(value);
  // Construct in local time from the parts, so toLocaleDateString formats the
  // day that was actually stored.
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString();
}

const MINUTE = 60000, HOUR = 3600000, DAY = 86400000;

// "3 days ago" / "in 2 months". Scanning a list for who has gone quiet is much
// easier on relative text than on a column of absolute dates; pair it with the
// absolute value in a title attribute, which is what titleFor() is for.
export function formatRelative(value) {
  const ms = toMs(value);
  if (ms === null) return null;
  const delta = Date.now() - ms;
  const abs = Math.abs(delta);
  const past = delta >= 0;
  const say = (n, unit) => {
    const plural = `${n} ${unit}${n === 1 ? '' : 's'}`;
    return past ? `${plural} ago` : `in ${plural}`;
  };

  if (abs < 45 * 1000) return past ? 'just now' : 'in a moment';
  if (abs < HOUR) return say(Math.round(abs / MINUTE), 'minute');
  if (abs < DAY) return say(Math.round(abs / HOUR), 'hour');
  if (abs < 7 * DAY) return say(Math.round(abs / DAY), 'day');
  if (abs < 30 * DAY) return say(Math.round(abs / (7 * DAY)), 'week');
  if (abs < 365 * DAY) return say(Math.round(abs / (30 * DAY)), 'month');
  return say(Math.round(abs / (365 * DAY)), 'year');
}

// Relative text for a calendar day, counted in whole days so "today" means the
// calendar today and not "within the last 24 hours".
export function formatRelativeDay(value) {
  if (!value) return null;
  const key = String(value).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((today - then) / DAY);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days === -1) return 'tomorrow';
  if (days > 0) return days < 7 ? `${days} days ago` : formatRelative(then.getTime());
  return `in ${Math.abs(days)} days`;
}

// The full value, for a title attribute behind relative text.
export function titleFor(value, { calendar = false } = {}) {
  return calendar ? formatCalendarDate(value) : formatInstant(value);
}

// Whole days between two server values, for "active for N days" style figures.
export function daysBetween(from, to = Date.now()) {
  const a = toMs(from), b = toMs(to) ?? to;
  if (a === null || b === null) return null;
  return Math.floor((b - a) / DAY);
}
